import { useQuery } from '@tanstack/react-query';
import { vehicleGroupsApi } from '../api/vehicle-groups';
import { vehiclesApi } from '../api/vehicles';
import { Button } from '../ui/Button';
import { inputClass } from '../ui/Field';

export interface CalendarFilterState {
  vehicleGroupId: string;
  /** Leere Menge heißt: alle. */
  vehicleIds: Set<string>;
}

/**
 * Einschränkung auf Fahrzeuggruppen und einzelne Fahrzeuge.
 *
 * Die Gruppe wird an die API durchgereicht, die Fahrzeugauswahl bleibt im
 * Browser: Der Endpunkt kennt keinen Fahrzeugfilter, und bei dieser Bestandsgröße
 * wäre er auch keiner — die Liste ist längst geladen, wenn die Auswahl fällt.
 */
export function CalendarFilters({
  value,
  onChange,
}: {
  value: CalendarFilterState;
  onChange: (next: CalendarFilterState) => void;
}) {
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: vehicleGroupsApi.list });
  const vehicles = useQuery({ queryKey: ['vehicles', { includeInactive: false }], queryFn: () => vehiclesApi.list(false) });

  const selectable = (vehicles.data ?? []).filter(
    (vehicle) => !value.vehicleGroupId || vehicle.vehicleGroup.id === value.vehicleGroupId,
  );

  const toggleVehicle = (id: string) => {
    const next = new Set(value.vehicleIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ...value, vehicleIds: next });
  };

  const hasFilter = value.vehicleGroupId !== '' || value.vehicleIds.size > 0;

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={value.vehicleGroupId}
          onChange={(event) =>
            // Beim Wechsel der Gruppe die Fahrzeugauswahl zurücksetzen — sonst
            // bliebe eine Auswahl aktiv, die in der neuen Gruppe gar nicht
            // vorkommt, und die Ansicht wäre unerklärlich leer.
            onChange({ vehicleGroupId: event.target.value, vehicleIds: new Set() })
          }
          className={`${inputClass} max-w-xs`}
          aria-label="Nach Fahrzeuggruppe filtern"
        >
          <option value="">Alle Fahrzeuggruppen</option>
          {groups.data?.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>

        {hasFilter && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange({ vehicleGroupId: '', vehicleIds: new Set() })}
          >
            Filter zurücksetzen
          </Button>
        )}
      </div>

      {selectable.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectable.map((vehicle) => {
            const active = value.vehicleIds.has(vehicle.id);
            return (
              <button
                key={vehicle.id}
                type="button"
                onClick={() => toggleVehicle(vehicle.id)}
                aria-pressed={active}
                className={[
                  'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
                  active
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: vehicle.colorHex }}
                  aria-hidden
                />
                {vehicle.licensePlate}
              </button>
            );
          })}

          {value.vehicleIds.size > 0 && (
            <span className="self-center pl-1 text-xs text-slate-400">
              {value.vehicleIds.size} von {selectable.length} ausgewählt
            </span>
          )}
        </div>
      )}
    </div>
  );
}
